/**
 * useStudentAssessmentsState — les gestes de la liste de « Mes contrôles » : la feuille de détail,
 * la suppression (confirmation, en cours, refus), la fermeture.
 *
 * Port de `SchoolAssessmentsScreen.js` + `SchoolAssessmentCard.js` : « Supprimer » demande « Supprimer
 * ce contrôle ? » puis `deleteSchoolAssessment { id }` ; le réducteur `schoolAssessments` retire la
 * ligne à la réponse. Ce que l'ancien écran taisait et que la page dit : la ligne qui s'estompe
 * pendant la suppression, et son refus. « Modifier » referme la feuille de détail et ouvre le
 * formulaire (`onEdit`, tenu par `useStudentAssessmentForm`). La croix : l'écran d'avant, ou le menu
 * si la pile est vide (`handleClose`).
 */
import { useCallback, useReducer } from "react"

import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UISchoolAssessment } from "../../../types"

type AssessmentId = UISchoolAssessment["id"]

export const DELETE_FAILED = "Le contrôle n'a pas pu être supprimé. Réessaie dans un instant."

export type StudentAssessmentsUi = {
    /** Le contrôle dont la feuille de détail est ouverte. */
    openId: AssessmentId | null
    /** Le contrôle dont la suppression attend sa confirmation. */
    deleteConfirmId: AssessmentId | null
    /** Le contrôle dont la suppression est partie. */
    deletingId: AssessmentId | null
    deleteError: string | null
}

export type StudentAssessmentsUiAction =
    | { type: "OPEN"; id: AssessmentId }
    | { type: "CLOSE" }
    | { type: "ASK_DELETE"; id: AssessmentId }
    | { type: "CANCEL_DELETE" }
    | { type: "DELETING"; id: AssessmentId }
    | { type: "DELETED" }
    | { type: "DELETE_FAILED"; error: string }
    | { type: "DISMISS_ERROR" }

export const initialStudentAssessmentsUi: StudentAssessmentsUi = {
    openId: null,
    deleteConfirmId: null,
    deletingId: null,
    deleteError: null,
}

export function studentAssessmentsUiReducer(
    state: StudentAssessmentsUi,
    action: StudentAssessmentsUiAction
): StudentAssessmentsUi {
    switch (action.type) {
        case "OPEN":
            return { ...state, openId: action.id }
        case "CLOSE":
            return { ...state, openId: null }
        case "ASK_DELETE":
            // La feuille se referme : la question se pose seule.
            return { ...state, openId: null, deleteConfirmId: action.id }
        case "CANCEL_DELETE":
            return { ...state, deleteConfirmId: null }
        case "DELETING":
            return { ...state, deleteConfirmId: null, deletingId: action.id, deleteError: null }
        case "DELETED":
            return { ...state, deletingId: null }
        case "DELETE_FAILED":
            return { ...state, deletingId: null, deleteError: action.error }
        case "DISMISS_ERROR":
            return { ...state, deleteError: null }
        default:
            return state
    }
}

export type UseStudentAssessmentsStateArgs = {
    /** « Modifier » : ouvre le formulaire prérempli. */
    onEdit: (id: AssessmentId) => void
}

export function useStudentAssessmentsState(args: UseStudentAssessmentsStateArgs) {
    const { onEdit } = args
    const [ui, dispatchUi] = useReducer(studentAssessmentsUiReducer, initialStudentAssessmentsUi)

    const { start } = useApiMutation({
        service: schoolAssessmentsService.deleteSchoolAssessment,
        voice: "student",
        fallback: DELETE_FAILED,
        // Plusieurs lignes visent cette tranche : `id` dit laquelle est la nôtre.
        scopeParam: "id",
        onSuccess: () => dispatchUi({ type: "DELETED" }),
        onError: (message) => dispatchUi({ type: "DELETE_FAILED", error: message }),
    })

    const onConfirmDelete = useCallback(() => {
        const id = ui.deleteConfirmId
        if (id == null) return
        dispatchUi({ type: "DELETING", id })
        start({ id })
    }, [start, ui.deleteConfirmId])

    const onEditAssessment = useCallback(
        (id: AssessmentId) => {
            dispatchUi({ type: "CLOSE" })
            onEdit(id)
        },
        [onEdit]
    )

    const onClose = useBackOr("menu")

    return {
        openAssessmentId: ui.openId,
        onOpenAssessment: (id: AssessmentId) => dispatchUi({ type: "OPEN", id }),
        onCloseAssessment: () => dispatchUi({ type: "CLOSE" }),
        onEditAssessment,
        onDeleteAssessment: (id: AssessmentId) => dispatchUi({ type: "ASK_DELETE", id }),
        deleteConfirmId: ui.deleteConfirmId,
        onConfirmDelete,
        onCancelDelete: () => dispatchUi({ type: "CANCEL_DELETE" }),
        deletingId: ui.deletingId,
        deleteError: ui.deleteError,
        onDismissDeleteError: () => dispatchUi({ type: "DISMISS_ERROR" }),
        onClose,
    }
}
